import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from './api';
import { queryKeys } from './queryClient';
import type {
  LicenseListItem,
  LicenseDetail,
  CreateLicense,
  UpdateLicense,
  CreateAllocation,
  LicenseAllocation,
  AllocationImportResult,
  AllocationImportRowError,
  LicenseComplianceOverview,
  PagedResult,
} from '../types';

/**
 * License list query parameters
 *
 * Supports filtering, sorting, and pagination for license queries
 */
export interface LicenseQueryParams {
  /** Search term for filtering by product name */
  search?: string;
  /** Filter by contract ID */
  contractId?: string;
  /** Filter by license type */
  licenseType?: string;
  /** Filter by active status */
  isActive?: boolean;
  /** Sort field name */
  sortBy?: string;
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Page number (1-indexed) */
  pageNumber?: number;
  /** Number of items per page */
  pageSize?: number;
}

/**
 * Wire shape of GET /licenses
 *
 * Unlike the contracts endpoint, the API wraps the license list as
 * `{ data, pagination }` rather than `PagedResult`. It is normalized in
 * `fetchLicenses` so every list consumer sees the same `PagedResult` shape.
 */
interface LicenseListResponse {
  data: LicenseListItem[];
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Fetch paginated list of licenses
 *
 * Retrieves licenses with optional filtering, sorting, and pagination.
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise resolving to paged license results
 */
const fetchLicenses = async (
  params?: LicenseQueryParams
): Promise<PagedResult<LicenseListItem>> => {
  const { data, pagination } = await api.get<LicenseListResponse>('/licenses', { params });
  return {
    items: data,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    totalPages: pagination.totalPages,
    totalCount: pagination.totalCount,
    hasNextPage: pagination.pageNumber < pagination.totalPages,
    hasPreviousPage: pagination.pageNumber > 1,
  };
};

/**
 * Fetch single license by ID
 *
 * Retrieves detailed information about a specific license (quantities and utilization;
 * the allocation rows come from `fetchLicenseAllocations`).
 *
 * @param id - License identifier
 * @returns Promise resolving to license detail
 */
const fetchLicense = async (id: string): Promise<LicenseDetail> => {
  return api.get<LicenseDetail>(`/licenses/${id}`);
};

/**
 * Fetch license compliance overview
 *
 * Retrieves aggregated compliance statistics across all licenses.
 *
 * @returns Promise resolving to compliance overview
 */
const fetchLicenseCompliance = async (): Promise<LicenseComplianceOverview> => {
  return api.get<LicenseComplianceOverview>('/licenses/compliance');
};

/**
 * Create a new license
 *
 * @param data - License creation data
 * @returns Promise resolving to created license
 */
const createLicense = async (data: CreateLicense): Promise<LicenseDetail> => {
  return api.post<LicenseDetail>('/licenses', data);
};

/**
 * Update an existing license
 *
 * @param id - License identifier
 * @param data - License update data
 * @returns Promise resolving to updated license
 */
const updateLicense = async (
  id: string,
  data: UpdateLicense
): Promise<LicenseDetail> => {
  return api.put<LicenseDetail>(`/licenses/${id}`, data);
};

/**
 * Delete a license
 *
 * @param id - License identifier
 * @returns Promise resolving when deletion is complete
 */
const deleteLicense = async (id: string): Promise<void> => {
  return api.delete<void>(`/licenses/${id}`);
};

/**
 * Fetch the active allocations of a license
 *
 * `GET /licenses/{id}` carries only a count, so the detail page reads the rows from here.
 *
 * @param licenseId - License identifier
 * @returns Promise resolving to the active allocations, newest first
 */
const fetchLicenseAllocations = async (licenseId: string): Promise<LicenseAllocation[]> => {
  return api.get<LicenseAllocation[]>(`/licenses/${licenseId}/allocations`);
};

/**
 * Turn an allocation endpoint failure into an Error carrying the server's reason
 *
 * The allocation endpoints answer 400/404 with `{ error }` (quantity exceeds availability,
 * inactive license, already deallocated, unknown allocation); `api.ts` only reads a
 * `message` property, so that text would otherwise be lost behind axios's generic message.
 */
const allocationError = (err: unknown, fallback: string): Error => {
  const serverMessage = axios.isAxiosError(err)
    ? (err.response?.data as { error?: string } | undefined)?.error
    : undefined;
  return new Error(serverMessage || fallback);
};

/**
 * Allocate units of a license to a user, department or cost center
 *
 * @param licenseId - License identifier
 * @param data - Allocation creation data
 * @returns Promise resolving to the created allocation; rejects with the server's reason
 */
const allocateLicense = async (
  licenseId: string,
  data: CreateAllocation
): Promise<LicenseAllocation> => {
  try {
    return await api.post<LicenseAllocation>(`/licenses/${licenseId}/allocations`, data);
  } catch (err) {
    throw allocationError(err, 'Failed to allocate license');
  }
};

/**
 * Deallocate an allocation of a license
 *
 * @param licenseId - License identifier
 * @param allocationId - Allocation identifier
 * @returns Promise resolving when deallocation is complete; rejects with the server's reason
 */
const deallocateLicense = async (licenseId: string, allocationId: string): Promise<void> => {
  try {
    await api.delete<void>(`/licenses/${licenseId}/allocations/${allocationId}`);
  } catch (err) {
    throw allocationError(err, 'Failed to deallocate license');
  }
};

/** Largest allocation import file the API accepts (5 MB); checked here before any request is sent */
export const MAX_ALLOCATION_IMPORT_BYTES = 5 * 1024 * 1024;

const IMPORT_TOO_LARGE = 'The file is larger than 5MB';

/**
 * A rejected allocation import
 *
 * `message` is the server's reason (or a local one for an oversized file); `rowErrors` lists the
 * per-row problems when the server found any, each with the spreadsheet row number (header = 1).
 */
export class AllocationImportError extends Error {
  readonly rowErrors: AllocationImportRowError[];

  constructor(message: string, rowErrors: AllocationImportRowError[]) {
    super(message);
    this.name = 'AllocationImportError';
    this.rowErrors = rowErrors;
  }
}

/**
 * Import allocations into a license from a CSV file
 *
 * Posts the file as multipart form data. Error shapes handled: the action's 400 `{ error, rowErrors }`,
 * 404 / 409 `{ error }`, MVC's automatic 400 `{ title }` and Kestrel's bodiless 413.
 *
 * @param licenseId - License identifier
 * @param file - The .csv file
 * @param replaceExisting - Deallocate the User allocations that are not in the file
 * @returns Promise resolving to the counts; rejects with an `AllocationImportError`
 */
const importLicenseAllocations = async (
  licenseId: string,
  file: File,
  replaceExisting: boolean,
): Promise<AllocationImportResult> => {
  if (file.size > MAX_ALLOCATION_IMPORT_BYTES) {
    throw new AllocationImportError(IMPORT_TOO_LARGE, []);
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('replaceExisting', replaceExisting ? 'true' : 'false');
  try {
    return await api.post<AllocationImportResult>(`/licenses/${licenseId}/allocations/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
  } catch (err) {
    if (!axios.isAxiosError(err)) {
      throw new AllocationImportError('Import failed', []);
    }
    if (err.response?.status === 413) {
      throw new AllocationImportError(IMPORT_TOO_LARGE, []);
    }
    const data = err.response?.data as
      | { error?: string; title?: string; rowErrors?: AllocationImportRowError[] }
      | undefined;
    throw new AllocationImportError(data?.error || data?.title || 'Import failed', data?.rowErrors ?? []);
  }
};

/**
 * Hook: Fetch paginated list of licenses
 *
 * Provides automatic caching, background refetching, and optimistic updates
 * for license list data. Supports filtering, sorting, and pagination.
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Query result with licenses data, loading state, and error state
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useLicenses({
 *   contractId: '123',
 *   isActive: true,
 *   pageNumber: 1,
 *   pageSize: 25,
 * });
 * ```
 */
export const useLicenses = (params?: LicenseQueryParams) => {
  return useQuery({
    queryKey: queryKeys.licenses.list(params),
    queryFn: () => fetchLicenses(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Fetch single license by ID
 *
 * Retrieves detailed license information (quantities, utilization, key, notes).
 * The query is disabled if no ID is provided.
 *
 * @param id - License identifier
 * @returns Query result with license detail, loading state, and error state
 *
 * @example
 * ```typescript
 * const { data: license, isLoading } = useLicense(licenseId);
 * if (license) {
 *   console.log(license.availableQuantity);
 * }
 * ```
 */
export const useLicense = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.licenses.detail(id!),
    queryFn: () => fetchLicense(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Fetch the active allocations of a license
 *
 * Keyed under the license's detail key, so the invalidation the mutation hooks issue
 * refetches both the license and its allocations. Disabled if no ID is provided.
 *
 * @param id - License identifier
 * @returns Query result with the allocation rows, loading state, and error state
 *
 * @example
 * ```typescript
 * const { data: allocations = [] } = useLicenseAllocations(licenseId);
 * ```
 */
export const useLicenseAllocations = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.licenses.allocations(id!),
    queryFn: () => fetchLicenseAllocations(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Fetch license compliance overview
 *
 * Retrieves aggregated compliance statistics for all licenses.
 *
 * @returns Query result with compliance data, loading state, and error state
 *
 * @example
 * ```typescript
 * const { data: compliance, isLoading } = useLicenseCompliance();
 * if (compliance) {
 *   console.log(`Compliance Rate: ${compliance.overallComplianceRate}%`);
 * }
 * ```
 */
export const useLicenseCompliance = () => {
  return useQuery({
    queryKey: ['licenses', 'compliance'],
    queryFn: fetchLicenseCompliance,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Create new license mutation
 *
 * Provides mutation function for creating licenses with automatic
 * cache invalidation and optimistic updates.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const createMutation = useCreateLicense();
 *
 * const handleSubmit = async (data: CreateLicense) => {
 *   try {
 *     const newLicense = await createMutation.mutateAsync(data);
 *     navigate(`/licenses/${newLicense.id}`);
 *   } catch (error) {
 *     console.error('Failed to create license:', error);
 *   }
 * };
 * ```
 */
export const useCreateLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLicense,
    onSuccess: () => {
      // Invalidate license list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.lists() });
      // Invalidate compliance data
      queryClient.invalidateQueries({ queryKey: ['licenses', 'compliance'] });
      // Seats and cost per user on contracts and the dashboard come from licenses (#17)
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Update license mutation
 *
 * Provides mutation function for updating licenses with automatic
 * cache invalidation and optimistic updates.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const updateMutation = useUpdateLicense();
 *
 * const handleUpdate = async (id: string, data: UpdateLicense) => {
 *   try {
 *     await updateMutation.mutateAsync({ id, data });
 *     toast.success('License updated successfully');
 *   } catch (error) {
 *     toast.error('Failed to update license');
 *   }
 * };
 * ```
 */
export const useUpdateLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLicense }) =>
      updateLicense(id, data),
    onSuccess: (updatedLicense) => {
      // Update the specific license in cache
      queryClient.setQueryData(
        queryKeys.licenses.detail(updatedLicense.id),
        updatedLicense
      );

      // Invalidate license list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.lists() });
      // Invalidate compliance data
      queryClient.invalidateQueries({ queryKey: ['licenses', 'compliance'] });
      // Seats and cost per user on contracts and the dashboard come from licenses (#17)
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Delete license mutation
 *
 * Provides mutation function for deleting licenses with automatic
 * cache invalidation.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const deleteMutation = useDeleteLicense();
 *
 * const handleDelete = async (id: string) => {
 *   if (confirm('Are you sure you want to delete this license?')) {
 *     try {
 *       await deleteMutation.mutateAsync(id);
 *       navigate('/licenses');
 *     } catch (error) {
 *       toast.error('Failed to delete license');
 *     }
 *   }
 * };
 * ```
 */
export const useDeleteLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLicense,
    onSuccess: (_, deletedId) => {
      // Remove the deleted license from cache
      queryClient.removeQueries({ queryKey: queryKeys.licenses.detail(deletedId) });

      // Invalidate license list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.lists() });
      // Invalidate compliance data
      queryClient.invalidateQueries({ queryKey: ['licenses', 'compliance'] });
      // Seats and cost per user on contracts and the dashboard come from licenses (#17)
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Allocate license mutation
 *
 * Provides mutation function for allocating units of a license with automatic
 * cache invalidation. The response is the created allocation, not the license, so the
 * license detail (and, through its key, the allocation list) is invalidated rather than
 * written.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const allocateMutation = useAllocateLicense();
 *
 * const handleAllocate = async (data: CreateAllocation) => {
 *   try {
 *     await allocateMutation.mutateAsync({ licenseId, data });
 *     toast.success('License allocated successfully');
 *   } catch (error) {
 *     toast.error((error as Error).message);
 *   }
 * };
 * ```
 */
export const useAllocateLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ licenseId, data }: { licenseId: string; data: CreateAllocation }) =>
      allocateLicense(licenseId, data),
    // The client default retries a failed mutation once. This POST is not idempotent: if the
    // server committed the allocation and only the response was lost, a retry would create a
    // second one (found by the Codex adversarial review). Fail instead; onSettled refetches
    // the truth and the user can see whether the allocation landed.
    retry: false,
    // onSettled, not onSuccess: a 400 ("insufficient licenses") means the cached quantities
    // are stale, and refetching them is what lets the user see why and retry sensibly
    onSettled: (_, __, { licenseId }) => {
      // Refetch the license quantities and its allocation rows (nested key)
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.detail(licenseId) });

      // Invalidate license list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.lists() });
      // Invalidate compliance data
      queryClient.invalidateQueries({ queryKey: ['licenses', 'compliance'] });
      // Seats and cost per user on contracts and the dashboard come from licenses (#17)
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Deallocate license mutation
 *
 * Provides mutation function for deallocating licenses from users with automatic
 * cache invalidation.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const deallocateMutation = useDeallocateLicense();
 *
 * const handleDeallocate = async (licenseId: string, allocationId: string) => {
 *   try {
 *     await deallocateMutation.mutateAsync({ licenseId, allocationId });
 *     toast.success('License deallocated successfully');
 *   } catch (error) {
 *     toast.error('Failed to deallocate license');
 *   }
 * };
 * ```
 */
export const useDeallocateLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ licenseId, allocationId }: { licenseId: string; allocationId: string }) =>
      deallocateLicense(licenseId, allocationId),
    // No retry: if the first DELETE committed and its response was lost, the retry would be
    // answered 400 "already deallocated" and report a success as an error
    retry: false,
    // onSettled: a 400 ("already deallocated") or 404 means the rows are stale; refetch them
    onSettled: (_, __, { licenseId }) => {
      // Refetch the license quantities and its allocation rows (nested key)
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.detail(licenseId) });

      // Invalidate license list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.lists() });
      // Invalidate compliance data
      queryClient.invalidateQueries({ queryKey: ['licenses', 'compliance'] });
      // Seats and cost per user on contracts and the dashboard come from licenses (#17)
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Import allocations from a CSV file mutation
 *
 * Resolves to the counts the API reports; rejects with an `AllocationImportError` carrying the
 * reason and any per-row problems, so the dialog can list them.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const importMutation = useImportLicenseAllocations();
 * try {
 *   const result = await importMutation.mutateAsync({ licenseId, file, replaceExisting: false });
 *   console.log(`${result.created} created`);
 * } catch (error) {
 *   if (error instanceof AllocationImportError) showRows(error.rowErrors);
 * }
 * ```
 */
export const useImportLicenseAllocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ licenseId, file, replaceExisting }: { licenseId: string; file: File; replaceExisting: boolean }) =>
      importLicenseAllocations(licenseId, file, replaceExisting),
    // Not idempotent in replace mode and not cheap: never retry a lost response
    retry: false,
    // onSettled: a 400 or 409 means the cached quantities may be stale; refetch them either way
    onSettled: (_, __, { licenseId }) => {
      // Refetch the license quantities and its allocation rows (nested key)
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.detail(licenseId) });

      // Invalidate license list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.licenses.lists() });
      // Invalidate compliance data
      queryClient.invalidateQueries({ queryKey: ['licenses', 'compliance'] });
      // Seats and cost per user on contracts and the dashboard come from licenses (#17); an
      // import changes the active allocations they fall back to when a license has no total
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};
